"use client";

import Image from "next/image";

/** Fixed IDs for payment methods. Pass these in the `items` prop to choose what to render. */
export type PaymentMethodId =
  | "mada"
  | "visa"
  | "apple-pay"
  | "mastercard"
  | "bank";

const PAYMENT_METHOD_MAP: Record<
  PaymentMethodId,
  { src: string; alt: string; width: number; height: number }
> = {
  mada: { src: "/payment/mada.svg", alt: "mada", width: 44, height: 24 },
  visa: { src: "/payment/visa.svg", alt: "VISA", width: 48, height: 24 },
  "apple-pay": {
    src: "/payment/apple-pay.svg",
    alt: "Apple Pay",
    width: 44,
    height: 24,
  },
  mastercard: {
    src: "/payment/mastercard.svg",
    alt: "Mastercard",
    width: 48,
    height: 24,
  },
  bank: { src: "/payment/bank.svg", alt: "Bank transfer", width: 34, height: 24 },
};

export type PaymentMethodsStripSize = "sm" | "md" | "lg";

const sizeClasses: Record<
  PaymentMethodsStripSize,
  { container: string; card: string }
> = {
  sm: {
    container: "gap-1",
    card: "h-8 w-14 max-lg:h-6 max-lg:w-10 max-lg:p-1.5",
  },
  md: {
    container: "gap-1",
    card: "h-10 w-[75px] max-lg:w-[52px] max-lg:h-[32px] max-lg:p-2",
  },
  lg: {
    container: "gap-2",
    card: "h-12 w-[90px] max-lg:h-10 max-lg:w-[65px] max-lg:p-2.5",
  },
};

export interface PaymentMethodsStripProps {
  /** Payment method IDs to display, in order. Use fixed names: "mada" | "visa" | "apple-pay" | "mastercard" | "bank". */
  items: PaymentMethodId[];
  /** Size preset for card and image container. Default: "md". */
  size?: PaymentMethodsStripSize;
  /** Optional class for the wrapper flex container. */
  className?: string;
  /** Optional class for each card wrapper. */
  cardClassName?: string;
}

export function PaymentMethodsStrip({
  items,
  size = "md",
  className = "",
  cardClassName = "",
}: PaymentMethodsStripProps) {
  const { container, card } = sizeClasses[size];

  return (
    <div
      className={`flex flex-wrap items-center ${container} ${className}`.trim()}
    >
      {items.map((id) => {
        const p = PAYMENT_METHOD_MAP[id];
        if (!p) return null;
        return (
          <div
            key={id}
            className={` rounded-xs flex items-center justify-center  ${card} ${cardClassName}`.trim()}
          >
            <Image
              src={p.src}
              alt={p.alt}
              width={p.width}
              height={p.height}
              className="object-contain w-full h-full"
              priority={false}
              // Local SVGs: next/image optimizer intermittently returns 400
              // ("isn't a valid image … received null") during/after deploys.
              unoptimized
            />
          </div>
        );
      })}
    </div>
  );
}
